// ------------------------------------------------------------------------------
// name: useThrottleQuery
// author: mudas
// created: 2025/10/22
// ------------------------------------------------------------------------------

import { useEffect, useState } from 'react';
import { throttle, ThrottleSettings } from 'lodash-es';
import { useCreation } from 'ahooks';

import { QueryKey } from '@tanstack/query-core';
import { UseQueryOptions } from '@tanstack/react-query';

export interface ThrottleQueryOptions {
  throttleWait?: number;
  throttleLeading?: boolean;
  throttleTrailing?: boolean;
}

export interface DataOptions {
  queryHash?: string;
  enabled?: boolean;
}

/**
 * 让 useQuery 支持节流查询
 * @see https://www.lodashjs.com/docs/lodash.throttle
 */
export const useThrottleQuery = <
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: DataOptions & ThrottleQueryOptions,
) => {
  const { throttleWait, throttleLeading, throttleTrailing, ...otherOptions } = options;

  const [queryOptions, setQueryOptions] = useState<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>>({});

  const throttleOptions = useCreation(() => {
    const ret: ThrottleSettings = {};
    if (throttleLeading !== undefined) {
      ret.leading = throttleLeading;
    }
    if (throttleTrailing !== undefined) {
      ret.trailing = throttleTrailing;
    }
    return ret;
  }, [throttleLeading, throttleTrailing]);

  const changeQueryOptions = useCreation(() => {
    const handler = (options) => {
      setQueryOptions?.({ queryHash: options.queryHash, enabled: options.enabled });
    };

    if (!throttleWait) return null;

    return throttle(handler, throttleWait, throttleOptions);
  }, [throttleWait, throttleOptions]);

  useEffect(() => {
    changeQueryOptions?.(otherOptions);
  }, [otherOptions.queryHash, otherOptions.enabled]);

  return [queryOptions, setQueryOptions];
};
